import React, { useState, useMemo, useEffect } from 'react';
import './AddParticipantFormPopup.scss';
import Rodal from '../../../../components/Rodal/Rodal';
import { ModalHeader, ModalBody, Button, Col, FormGroup, Row } from 'reactstrap';
import { Form, Field } from '@availity/form';
import * as yup from 'yup';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import { useFormikContext } from 'formik';
import BootstrapTable from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import { MarketplaceBuyerParticipantContract } from '../../../../slices/marketplace/meetings/models/marketplace-buyer-participant-contract';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';

export interface AddParticipantFormValues {
    name: string;
    role: string;
    shareToList: boolean;
}

interface AddParticipantFormPopupProps {
    isVisible: boolean;
    onClose: () => void;
    onSubmit: (values: AddParticipantFormValues) => Promise<void>;
    isSubmitting: boolean;
    buyerParticipants?: MarketplaceBuyerParticipantContract[];
    initialSelectedBuyerIds?: number[];
    onSyncBuyerParticipants?: (selectedIds: number[]) => Promise<void>;
    onDeleteBuyerParticipant?: (id: number) => Promise<void>;
    onUpdateBuyerParticipant?: (participant: { id: number; name: string; role: string }) => Promise<void>;
    canManageGlobalParticipants?: boolean;
}

const addParticipantSchema = yup.object().shape({
    name: yup.string().required('Name is required'),
    role: yup.string().required('Role is required'),
});

const FormSync = ({
    editingParticipant,
    resetTrigger,
}: {
    editingParticipant: MarketplaceBuyerParticipantContract | null;
    resetTrigger: number;
}): null => {
    const { resetForm } = useFormikContext<AddParticipantFormValues>();
    useEffect(() => {
        if (editingParticipant) {
            resetForm({ values: { name: editingParticipant.name, role: editingParticipant.role, shareToList: false } });
        } else {
            resetForm({ values: { name: '', role: '', shareToList: false } });
        }
    }, [editingParticipant, resetTrigger]);
    return null;
};

const SaveEditButton = ({
    editingParticipant,
    onSave,
    isSaving,
}: {
    editingParticipant: MarketplaceBuyerParticipantContract;
    onSave: (p: { id: number; name: string; role: string }) => Promise<void>;
    isSaving: boolean;
}) => {
    const { values } = useFormikContext<AddParticipantFormValues>();
    return (
        <ButtonLoader
            type="button"
            className="btn btn-primary"
            isLoading={isSaving}
            disabled={isSaving}
            buttonText="Save"
            loaderButtonText=""
            onClick={() => onSave({ id: editingParticipant.id, name: values.name, role: values.role })}
        />
    );
};

interface BuyerParticipantSelectorProps {
    participants: MarketplaceBuyerParticipantContract[];
    initialSelectedIds: number[];
    onSync: (selectedIds: number[]) => Promise<void>;
    editingId: number | null;
    onStartEdit: (participant: MarketplaceBuyerParticipantContract) => void;
    onRequestDelete: (participant: MarketplaceBuyerParticipantContract) => void;
    canManageGlobalParticipants?: boolean;
}

const BuyerParticipantSelector = ({
    participants,
    initialSelectedIds,
    onSync,
    editingId,
    onStartEdit,
    onRequestDelete,
    canManageGlobalParticipants,
}: BuyerParticipantSelectorProps) => {
    const [selectedIds, setSelectedIds] = useState<number[]>(initialSelectedIds);
    const [isSyncing, setIsSyncing] = useState(false);

    useEffect(() => {
        setSelectedIds(initialSelectedIds);
    }, [initialSelectedIds]);

    const isDirty = useMemo(() => {
        if (selectedIds.length !== initialSelectedIds.length) return true;
        const initial = new Set(initialSelectedIds);
        return selectedIds.some((id) => !initial.has(id));
    }, [selectedIds, initialSelectedIds]);

    const handleCheckboxChange = (id: number) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleSyncSave = async () => {
        setIsSyncing(true);
        try {
            await onSync(selectedIds);
        } finally {
            setIsSyncing(false);
        }
    };

    const tableData = useMemo(
        () => participants.map((p) => ({ ...p, selected: selectedIds.includes(p.id), isEditing: p.id === editingId })),
        [participants, selectedIds, editingId]
    );

    const columns = [
        {
            dataField: 'selected',
            text: '',
            headerClasses: 'select-column-header',
            formatter: (_: any, row: MarketplaceBuyerParticipantContract & { selected: boolean }) => (
                <input
                    type="checkbox"
                    checked={row.selected}
                    onChange={() => handleCheckboxChange(row.id)}
                />
            ),
        },
        { dataField: 'name', text: 'Name' },
        { dataField: 'role', text: 'Role' },
        ...(canManageGlobalParticipants ? [{
            dataField: 'actions',
            text: 'Actions',
            isDummyField: true,
            headerClasses: 'actions-column-header',
            formatter: (_: any, row: MarketplaceBuyerParticipantContract & { isEditing: boolean }) => (
                <div className="action-icons">
                    <FontAwesomeIcon
                        icon={faEdit}
                        onClick={() => onStartEdit(row)}
                        title="Edit participant"
                        style={row.isEditing ? { color: '#545cd8' } : undefined}
                    />
                    <FontAwesomeIcon
                        className="delete-button"
                        icon={faTrashCan}
                        onClick={() => onRequestDelete(row)}
                        title="Delete participant"
                    />
                </div>
            ),
        }] : []),
    ];

    return (
        <div className="buyer-participants-selector">
            <BootstrapTable
                bootstrap4
                striped
                bordered
                keyField="id"
                data={tableData}
                columns={columns}
                noDataIndication="No participants found"
                rowStyle={{ height: '36px' }}
                rowClasses={(row: any) => row.isEditing ? 'editing-row' : ''}
            />
            {isDirty && (
                <div className="buyer-participants-sync">
                    <ButtonLoader
                        type="button"
                        className="btn btn-primary"
                        isLoading={isSyncing}
                        disabled={isSyncing}
                        buttonText="Save"
                        loaderButtonText=""
                        onClick={handleSyncSave}
                    />
                </div>
            )}
        </div>
    );
};

const ShareToListCheckbox = () => {
    const { values, setFieldValue } = useFormikContext<AddParticipantFormValues>();
    return (
        <FormGroup className="share-to-list-group">
            <input
                type="checkbox"
                id="shareToList"
                checked={values.shareToList}
                onChange={(e) => setFieldValue('shareToList', e.target.checked)}
            />
            <label htmlFor="shareToList" className="share-to-list-label">
                Do you want to add this participant to the list shared between other deals?
            </label>
        </FormGroup>
    );
};

const AddParticipantFormPopup = (props: AddParticipantFormPopupProps) => {
    const isBuyer = props.buyerParticipants !== undefined;
    const hasBuyerParticipants = isBuyer && props.buyerParticipants!.length > 0;
    const [editingParticipant, setEditingParticipant] = useState<MarketplaceBuyerParticipantContract | null>(null);
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [participantToDelete, setParticipantToDelete] = useState<MarketplaceBuyerParticipantContract | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [formResetTrigger, setFormResetTrigger] = useState(0);

    const handleCancelEdit = () => setEditingParticipant(null);

    const handleSaveEdit = async (participant: { id: number; name: string; role: string }) => {
        if (!props.onUpdateBuyerParticipant) return;
        setIsSavingEdit(true);
        try {
            await props.onUpdateBuyerParticipant(participant);
            setEditingParticipant(null);
            setFormResetTrigger((t) => t + 1);
        } finally {
            setIsSavingEdit(false);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!participantToDelete || !props.onDeleteBuyerParticipant) return;
        setIsDeleting(true);
        try {
            await props.onDeleteBuyerParticipant(participantToDelete.id);
            setParticipantToDelete(null);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="add-participant-form-popup">
            <Rodal
                width={550}
                visible={props.isVisible && participantToDelete === null}
                animation={'slideRight'}
                onClose={props.onClose}
                center>
                <ModalHeader>Add New Participant</ModalHeader>
                <ModalBody className="body_white_box">
                    <Form
                        key="add-participant-form"
                        initialValues={{ name: '', role: '', shareToList: false }}
                        validationSchema={addParticipantSchema}
                        onSubmit={async (values: AddParticipantFormValues) => {
                            await props.onSubmit(values);
                            setFormResetTrigger((t) => t + 1);
                        }}>
                        <FormSync editingParticipant={editingParticipant} resetTrigger={formResetTrigger} />
                        <Col>
                            {hasBuyerParticipants && (
                                <FormGroup>
                                    <BuyerParticipantSelector
                                        participants={props.buyerParticipants!}
                                        initialSelectedIds={props.initialSelectedBuyerIds ?? []}
                                        onSync={props.onSyncBuyerParticipants!}
                                        editingId={editingParticipant?.id ?? null}
                                        onStartEdit={setEditingParticipant}
                                        onRequestDelete={setParticipantToDelete}
                                        canManageGlobalParticipants={props.canManageGlobalParticipants}
                                    />
                                </FormGroup>
                            )}
                            <FormGroup>
                                <Row>
                                    <Col md={12}>
                                        <label>Name</label>
                                        <Field type="text" name="name" id="name" className="form-control" />
                                    </Col>
                                </Row>
                            </FormGroup>
                            <FormGroup>
                                <Row>
                                    <Col md={12}>
                                        <label>Role</label>
                                        <Field type="text" name="role" id="role" className="form-control" />
                                    </Col>
                                </Row>
                            </FormGroup>
                            {isBuyer && !editingParticipant && <ShareToListCheckbox />}
                        </Col>
                        <Col className="footer">
                            {editingParticipant ? (
                                <>
                                    <Button className="cancel btn btn-secondary" onClick={handleCancelEdit}>
                                        Cancel
                                    </Button>
                                    <SaveEditButton
                                        editingParticipant={editingParticipant}
                                        onSave={handleSaveEdit}
                                        isSaving={isSavingEdit}
                                    />
                                </>
                            ) : (
                                <>
                                    <Button className="cancel btn btn-secondary" onClick={() => props.onClose()}>
                                        Cancel
                                    </Button>
                                    <ButtonLoader
                                        type="submit"
                                        className="btn btn-primary loader-submit"
                                        disabled={props.isSubmitting}
                                        isLoading={props.isSubmitting}
                                        buttonText="Add Participant"
                                        loaderButtonText=""
                                        onClick={() => {}}
                                    />
                                </>
                            )}
                        </Col>
                    </Form>
                </ModalBody>
            </Rodal>

            <ConfirmationModal
                isVisible={participantToDelete !== null}
                setIsVisible={(v: boolean) => !v && setParticipantToDelete(null)}
                header="Delete Participant"
                confirmationText={`Are you sure you want to delete '${participantToDelete?.name}'? This will remove them from all meetings.`}
                onConfirm={handleDeleteConfirm}
                nextButtonText="Delete"
                cancelButtonText="Cancel"
                isLoading={isDeleting}
            />
        </div>
    );
};

export default AddParticipantFormPopup;
